// Public-client OAuth token endpoint: code exchange, refresh and revoke. JSON bodies, no Authorization header, no secret.

import {
  OAUTH_CLIENT_ID,
  oauthRevokeUrl,
  oauthTokenUrl,
  redirectUri,
} from 'services/auth/constants';

export type TokenResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope?: string;
};

export type OAuthErrorCode =
  | 'invalid_grant'
  | 'invalid_client'
  | 'invalid_request'
  | 'rate_limited'
  | 'network'
  | 'unknown';

export class OAuthError extends Error {
  code: OAuthErrorCode;

  status?: number;

  constructor(code: OAuthErrorCode, message?: string, status?: number) {
    super(message ?? code);
    this.name = 'OAuthError';
    this.code = code;
    this.status = status;
    Object.setPrototypeOf(this, OAuthError.prototype);
  }
}

const REQUEST_TIMEOUT_MS = 10_000;

const HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

const BODY_ERROR_CODES: Record<string, OAuthErrorCode> = {
  invalid_grant: 'invalid_grant',
  invalid_client: 'invalid_client',
  invalid_request: 'invalid_request',
};

const readJson = async (response: Response): Promise<any> => {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
};

const classifyFailure = (status: number, body: any): OAuthError => {
  const bodyError = typeof body?.error === 'string' ? body.error : undefined;
  const message = typeof body?.error_description === 'string' ? body.error_description : bodyError;
  if (status === 429) return new OAuthError('rate_limited', message, status);
  const code = (bodyError && BODY_ERROR_CODES[bodyError]) || 'unknown';
  return new OAuthError(code, message ?? `HTTP ${status}`, status);
};

const post = async (url: string, payload: Record<string, unknown>): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (error) {
    const aborted = (error as { name?: string } | null)?.name === 'AbortError';
    throw new OAuthError(aborted ? 'unknown' : 'network', aborted ? 'Request aborted' : 'Network error');
  } finally {
    clearTimeout(timer);
  }
};

const postForTokens = async (url: string, payload: Record<string, unknown>): Promise<TokenResponse> => {
  const response = await post(url, payload);
  const body = await readJson(response);
  if (!response.ok) throw classifyFailure(response.status, body);
  if (typeof body?.access_token !== 'string') {
    throw new OAuthError('unknown', 'Malformed token response', response.status);
  }
  return body as TokenResponse;
};

export const exchangeCode = ({
  code,
  verifier,
  stateSession,
  clientId,
}: {
  code: string;
  verifier: string;
  stateSession?: string;
  clientId?: string;
}): Promise<TokenResponse> => {
  const resolvedClientId = clientId ?? localStorage.getItem(OAUTH_CLIENT_ID);
  if (!resolvedClientId) {
    return Promise.reject(new OAuthError('invalid_client', 'No registered client id'));
  }
  return postForTokens(oauthTokenUrl(), {
    grant_type: 'authorization_code',
    code,
    client_id: resolvedClientId,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
    ...(stateSession ? { state_session: stateSession } : {}),
  });
};

export const refreshTokens = ({
  clientId,
  refreshToken,
}: {
  clientId: string;
  refreshToken: string;
}): Promise<TokenResponse> => postForTokens(oauthTokenUrl(), {
  grant_type: 'refresh_token',
  refresh_token: refreshToken,
  client_id: clientId,
});

export const revokeToken = async ({
  clientId,
  token,
}: {
  clientId: string;
  token: string;
}): Promise<void> => {
  const response = await post(oauthRevokeUrl(), { token, client_id: clientId });
  if (!response.ok) throw classifyFailure(response.status, await readJson(response));
};
